import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronDown } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

const Icons = LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>;

// Popular icons for quick access — 100 icons
const POPULAR_ICON_NAMES = [
  'Heart', 'Star', 'Check', 'X', 'ArrowRight', 'ArrowLeft', 'ChevronRight', 'ChevronDown',
  'ChevronUp', 'ChevronLeft', 'ArrowUp', 'ArrowDown', 'CornerDownRight', 'ExternalLink',
  'Phone', 'Mail', 'MessageCircle', 'MessageSquare', 'Send', 'Share2', 'AtSign', 'Inbox',
  'MapPin', 'Clock', 'Calendar', 'Globe', 'Map', 'Navigation', 'Compass', 'Timer',
  'User', 'Users', 'UserCheck', 'UserPlus', 'ThumbsUp', 'ThumbsDown', 'Smile', 'Frown',
  'ShoppingCart', 'ShoppingBag', 'CreditCard', 'Truck', 'Package', 'Gift', 'Tag', 'Percent',
  'DollarSign', 'Receipt', 'Wallet', 'Store',
  'Home', 'Search', 'Settings', 'Bell', 'Eye', 'EyeOff', 'Lock', 'Unlock',
  'Download', 'Upload', 'Bookmark', 'Flag', 'Filter', 'RefreshCw', 'RotateCw', 'Trash2',
  'Image', 'Video', 'Music', 'Camera', 'Play', 'Pause', 'Volume2', 'Mic',
  'Film', 'Headphones',
  'File', 'FileText', 'Folder', 'Database', 'BarChart3', 'PieChart', 'TrendingUp', 'Activity',
  'Award', 'Shield', 'ShieldCheck', 'AlertTriangle', 'Info', 'HelpCircle', 'CheckCircle', 'XCircle',
  'Sun', 'Moon', 'Cloud', 'Zap', 'Flame', 'Droplets', 'Leaf', 'Sparkles',
  'Wifi', 'Smartphone', 'Monitor', 'Cpu', 'Code', 'Terminal', 'Layers', 'GitBranch',
  'Lightbulb', 'Rocket', 'Target', 'Crosshair', 'Fingerprint', 'Key',
];

function isSelectableIconName(name: string): boolean {
  return Boolean(
    Icons[name] &&
    name !== 'createLucideIcon' &&
    name !== 'default' &&
    name !== 'icons' &&
    !name.startsWith('__') &&
    !name.startsWith('Lucide') &&
    !name.endsWith('Icon') &&
    /^[A-Z]/.test(name),
  );
}

function normalizeIconName(value: string): string {
  const compact = value.trim().replace(/[^a-z0-9]/gi, '').toLowerCase();
  if (!compact) return '';
  return Object.keys(Icons).find(name => isSelectableIconName(name) && name.toLowerCase() === compact) || '';
}

function isShortcodeValue(value: string): boolean {
  return /^\[[a-zA-Z0-9_:-]+\]$/.test(value.trim());
}

interface IconPickerFieldProps {
  value: string;
  variant?: 'compact' | 'form';
  id?: string;
  onChange: (iconName: string) => void;
}

export function IconPickerField({ value, onChange, variant = 'compact', id }: IconPickerFieldProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [manualIconName, setManualIconName] = useState(value || '');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 264 });

  useEffect(() => {
    setManualIconName(value || '');
  }, [value]);

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = Math.min(rect.width, 280);
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 300;
      const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
      setDropdownPos({
        top: showAbove ? rect.top - dropdownHeight : rect.bottom + 4,
        left: rect.left,
        width: dropdownWidth,
      });
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setSearch('');
      }
    }
    function handleScroll() { updatePosition(); }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [open, updatePosition]);

  const filtered = useMemo(() => {
    if (!search) return POPULAR_ICON_NAMES;
    return Object.keys(Icons).filter(
      (name) =>
        isSelectableIconName(name) &&
        name.toLowerCase().includes(search.toLowerCase())
    ).slice(0, 80);
  }, [search]);

  const SelectedIcon = value && !isShortcodeValue(value) ? Icons[value] : null;
  const manualMatch = normalizeIconName(manualIconName);
  const hasManualInput = manualIconName.trim().length > 0;
  const manualShortcode = isShortcodeValue(manualIconName);
  const manualInvalid = hasManualInput && !manualMatch && !manualShortcode;

  const applyManualIconName = (inputValue = manualIconName): boolean => {
    const trimmed = inputValue.trim();
    if (isShortcodeValue(trimmed)) {
      onChange(trimmed);
      setManualIconName(trimmed);
      return true;
    }
    const matchedName = normalizeIconName(trimmed);
    if (!matchedName) return false;
    onChange(matchedName);
    setManualIconName(matchedName);
    return true;
  };

  return (
    <>
      <button
        ref={buttonRef}
        id={id}
        aria-expanded={open}
        type="button"
        onClick={() => { setOpen(!open); if (!open) setSearch(''); }}
        className={variant === 'form'
          ? 'input flex items-center gap-2 text-sm'
          : 'w-full flex items-center gap-2 px-2 py-1.5 bg-surface-hover border border-border rounded text-xs hover:border-border-strong transition-colors'}
      >
        {SelectedIcon ? (
          <>
            <SelectedIcon className="w-4 h-4 text-accent-primary" />
            <span className="flex-1 text-left">{value}</span>
          </>
        ) : value ? (
          <span className="flex-1 text-left text-text-primary truncate">{value}</span>
        ) : (
          <span className="flex-1 text-left text-text-secondary">Wybierz ikonę...</span>
        )}
        <ChevronDown className={`w-3 h-3 text-text-secondary transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && createPortal(
        <div
          ref={dropdownRef}
          className="bg-surface-raised border border-border rounded-lg shadow-2xl"
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 99999,
          }}
        >
          <div className="p-2 border-b border-border-subtle">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-text-muted" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Szukaj ikony..."
                className="w-full pl-7 pr-7 py-1 bg-surface-hover border border-border rounded text-xs text-text-primary"
                autoFocus
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                  <X className="w-3 h-3 text-text-muted" />
                </button>
              )}
            </div>
          </div>
          <div className="p-2 max-h-48 overflow-y-auto grid grid-cols-6 gap-1">
            {filtered.map((name) => {
              const IconComp = Icons[name];
              if (!IconComp) return null;
              return (
                <button
                  key={name}
                  onClick={() => { onChange(name); setOpen(false); setSearch(''); }}
                  className={`p-1.5 rounded hover:bg-surface-hover/60 transition-colors flex items-center justify-center ${
                    value === name ? 'bg-accent-primary/20 text-accent-primary' : 'text-text-secondary'
                  }`}
                  title={name}
                >
                  <IconComp className="w-4 h-4" />
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="col-span-6 text-center text-xs text-text-muted py-4">Brak wyników</div>
            )}
          </div>
          <div className="p-2 border-t border-border-subtle space-y-2">
            <div>
              <label className="block text-[10px] text-text-muted mb-1">Nazwa ikony</label>
              <input
                type="text"
                value={manualIconName}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setManualIconName(nextValue);
                  if (isShortcodeValue(nextValue)) {
                    onChange(nextValue.trim());
                    return;
                  }
                  const matchedName = normalizeIconName(nextValue);
                  if (matchedName) onChange(matchedName);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (applyManualIconName()) {
                      setOpen(false);
                      setSearch('');
                    }
                  }
                }}
                onBlur={() => applyManualIconName()}
                placeholder="np. ShieldCheck"
                className={`w-full px-2 py-1 bg-surface-hover border rounded text-xs text-text-primary focus:outline-none ${
                  manualInvalid ? 'border-danger/60 focus:border-danger' : 'border-border focus:border-accent-primary/50'
                }`}
              />
              {manualInvalid && (
                <p className="mt-1 text-[10px] text-danger">Nie znaleziono takiej ikony Lucide ani poprawnego shortcode.</p>
              )}
            </div>
            {value && (
              <button
                onClick={() => { onChange(''); setOpen(false); }}
                className="text-[10px] text-danger hover:text-danger"
              >
                Usuń ikonę
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
